import { useCallback } from 'react';
import { useI18n } from './provider';

/**
 * Formatos regionales.
 *
 * Todo pasa por `Intl` y por la etiqueta completa del navegador. Es lo que
 * hace que la misma fecha se escriba `14/8/2026` para un colombiano y
 * `8/14/2026` para un estadounidense sin que exista una línea de código por
 * país — y donde más se nota si un producto está traducido o localizado.
 */
export function useFormats() {
  const { formatLocale } = useI18n();

  return {
    date: useCallback(
      (value: string | Date) =>
        new Intl.DateTimeFormat(formatLocale, { dateStyle: 'medium' }).format(new Date(value)),
      [formatLocale],
    ),

    dateTime: useCallback(
      (value: string | Date) =>
        new Intl.DateTimeFormat(formatLocale, {
          dateStyle: 'medium',
          timeStyle: 'short',
        }).format(new Date(value)),
      [formatLocale],
    ),

    number: useCallback(
      (value: number) => new Intl.NumberFormat(formatLocale).format(value),
      [formatLocale],
    ),

    /**
     * Precios en la moneda que declara el plan.
     *
     * No se convierte a moneda local: haría falta un tipo de cambio y una
     * decisión fiscal sobre en qué moneda se factura. Mostrar el importe real
     * que se va a cobrar es más honesto que una conversión aproximada.
     */
    money: useCallback(
      (cents: number, currency = 'USD') =>
        new Intl.NumberFormat(formatLocale, { style: 'currency', currency }).format(cents / 100),
      [formatLocale],
    ),

    /**
     * Mes y año, para agrupar la galería.
     *
     * Sin día: el encabezado de un grupo mensual con un día suelto dentro se
     * lee como la fecha de una foto concreta, que es justo lo que no es.
     */
    month: useCallback(
      (value: string | Date) =>
        new Intl.DateTimeFormat(formatLocale, { month: 'long', year: 'numeric' }).format(
          new Date(value),
        ),
      [formatLocale],
    ),

    /** Tamaños en base 1024, con el separador decimal del idioma. */
    bytes: useCallback(
      (value: number) => {
        if (value <= 0) return '0 B';
        const unidades = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
        const escala = Math.min(Math.floor(Math.log(value) / Math.log(1024)), unidades.length - 1);
        const cantidad = value / 1024 ** escala;
        const decimales = cantidad >= 100 || escala === 0 ? 0 : 1;

        return `${new Intl.NumberFormat(formatLocale, {
          maximumFractionDigits: decimales,
          minimumFractionDigits: decimales,
        }).format(cantidad)} ${unidades[escala]}`;
      },
      [formatLocale],
    ),
  };
}
